import { useState, useEffect } from "react";
import {
  CheckCircle, AlertCircle, Clock, FileText, FileCheck,
  BookOpen, Map, Target, TrendingUp, Award, ChevronRight,
  Lock, User, Phone, Mail, Briefcase, Calendar,
  Check, Edit2, Save, X, DollarSign, BarChart2,
} from "lucide-react";

import SignNowStep               from "../components/SignNowStep";
import { getToken, authHeaders } from "../lib/auth";
import { formatDate, formatCurrency, formatPct } from "../lib/format";
import { ONBOARDING_DOCS, allDocsSigned, docKeyToOnboardingKey } from "../lib/signnow";

// ─── Spin keyframe (consumed by SignNowStep's Loader2) ────────────────────────
const SPIN_STYLE = <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>;

// ─── Avatar ───────────────────────────────────────────────────────────────────

function Avatar({ name, email, size = 52 }) {
  const letter = (name?.[0] ?? email?.[0] ?? "?").toUpperCase();
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: "linear-gradient(135deg, #1e40af, #2563eb)",
      color: "#fff", fontSize: size * 0.38, fontWeight: 700,
      display: "flex", alignItems: "center", justifyContent: "center",
      flexShrink: 0, boxShadow: "0 2px 8px rgba(37,99,235,0.25)",
    }}>
      {letter}
    </div>
  );
}

// ─── Mock data (swap for real API / context) ──────────────────────────────────

const MOCK_USER = {
  id: "usr_001",
  full_name: "Alex Rivera",
  email: "alex.rivera@propertunity.com",
  phone: "+1 (305) 555-0142",
  title: "Sales Representative",
  role: "sales_rep",
  registered_at: "2024-11-01T00:00:00Z",
  onboarding: {
    contract_signed: true,
    nda_signed: false,
    tax_form_signed: false,
  },
};

const MOCK_GOALS = {
  monthly_calls: 40, monthly_loans: 3, monthly_revenue: 25000,
  ytd_calls: 187,    ytd_loans: 11,    ytd_revenue: 94500,
  current_month_calls: 18, current_month_loans: 1, current_month_revenue: 8200,
};

const TRAINING_MODULES = [
  { id: "tm_01", title: "Propertunity 101",            subtitle: "Company overview & culture",              duration: "15 min", status: "completed",   color: "#2563eb", lessons: ["Who we are", "Lem Lux Realty partnership", "Your role in the ecosystem"] },
  { id: "tm_02", title: "Bridge Loans Deep Dive",      subtitle: "Short-term financing for flips & construction", duration: "25 min", status: "completed",   color: "#0ea5e9", lessons: ["What is a bridge loan?", "Qualifying criteria", "Common objections & rebuttals", "Underwriting basics"] },
  { id: "tm_03", title: "Term Loans & Buy-to-Rent",    subtitle: "Long-term DSCR & rental financing",       duration: "20 min", status: "in_progress", color: "#7c3aed", lessons: ["DSCR explained", "Rental portfolio strategy", "Rate & term structure", "Qualifying investors"] },
  { id: "tm_04", title: "The Discovery Call Playbook", subtitle: "Qualify, pitch, and close in 20 minutes", duration: "30 min", status: "locked",      color: "#16a34a", lessons: ["Opening frameworks", "Needs assessment questions", "Presenting loan options", "Handling stalls"] },
  { id: "tm_05", title: "CRM Mastery",                 subtitle: "Work your pipeline like a pro",           duration: "20 min", status: "locked",      color: "#ea580c", lessons: ["Pipeline stages", "Activity logging", "Follow-up cadences", "Reporting & forecasting"] },
  { id: "tm_06", title: "Compliance & Fair Lending",   subtitle: "Required — legal & regulatory basics",    duration: "35 min", status: "locked",      color: "#dc2626", required: true, lessons: ["RESPA overview", "Fair lending laws", "Privacy & data handling", "Do's and don'ts"] },
];

const ROADMAP_MILESTONES = [
  { week: "Week 1",  label: "Orientation",     desc: "Complete company & document onboarding",         done: true  },
  { week: "Week 2",  label: "Product Training", desc: "Finish Bridge + Term loan modules",              done: true  },
  { week: "Week 3",  label: "Shadow Calls",     desc: "Join 3 live discovery calls with your manager", done: false },
  { week: "Week 4",  label: "First Solo Call",  desc: "Run a discovery call independently",            done: false },
  { week: "Month 2", label: "First Close",      desc: "Close your first funded loan",                  done: false },
  { week: "Month 3", label: "Ramp Complete",    desc: "Consistently hitting monthly targets",          done: false },
];

// ─── Shared: locked placeholder ───────────────────────────────────────────────

function LockedPlaceholder({ title, desc }) {
  return (
    <div className="placeholder-box" style={{ marginTop: 8 }}>
      <div className="placeholder-icon" style={{ background: "#fef9c3" }}>
        <Lock size={26} color="#ca8a04" />
      </div>
      <div className="placeholder-title">{title}</div>
      <p className="placeholder-desc">{desc}</p>
      <button className="btn btn-primary" onClick={() => window.location.href = "/onboarding"}>
        Complete Onboarding <ChevronRight size={14} />
      </button>
    </div>
  );
}

// ─── Tab: Overview ────────────────────────────────────────────────────────────

function OverviewTab({ user, onEditSave }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    full_name: user?.full_name ?? "",
    phone:     user?.phone ?? "",
    title:     user?.title ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);
  const [error,  setError]  = useState("");

  const signedCount   = ONBOARDING_DOCS.filter(d => user?.onboarding?.[d.onboardingKey]).length;
  const totalDocs     = ONBOARDING_DOCS.length;
  const onboardingPct = Math.round((signedCount / totalDocs) * 100);

  async function handleSave() {
    setSaving(true); setError("");
    try {
      const res = await fetch("/api/users", {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(form),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || "Save failed"); }
      setSaved(true);
      setEditing(false);
      onEditSave?.(form);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      {/* Profile card */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">My Profile</span>
          {!editing
            ? <button className="btn btn-secondary btn-sm" onClick={() => setEditing(true)}><Edit2 size={13} /> Edit</button>
            : <div style={{ display: "flex", gap: 8 }}>
                <button className="btn btn-primary btn-sm" disabled={saving} onClick={handleSave}><Save size={13} /> {saving ? "Saving…" : "Save"}</button>
                <button className="btn btn-secondary btn-sm" onClick={() => setEditing(false)}><X size={13} /> Cancel</button>
              </div>
          }
        </div>
        <div className="card-body">
          <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 24 }}>
            <Avatar name={user?.full_name} email={user?.email} size={64} />
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#0f172a" }}>{user?.full_name ?? "—"}</div>
              <div style={{ fontSize: 13, color: "#64748b", marginTop: 2 }}>{user?.title ?? "Sales Representative"}</div>
              <span className={`badge badge-${user?.role ?? "sales_rep"}`} style={{ marginTop: 6, display: "inline-flex" }}>
                {(user?.role ?? "sales_rep").replace("_", " ")}
              </span>
            </div>
          </div>

          {error && <p className="settings-error"><AlertCircle size={14} />{error}</p>}
          {saved && <p className="settings-success"><Check size={14} />Profile updated</p>}

          <div className="form-row">
            <div className="form-group">
              <label className="form-label"><User size={13} style={{ display: "inline", marginRight: 5 }} />Full Name</label>
              {editing
                ? <input className="form-input" value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} />
                : <div style={{ padding: "8px 0", fontSize: 13.5, color: "#1e293b" }}>{user?.full_name ?? "—"}</div>
              }
            </div>
            <div className="form-group">
              <label className="form-label"><Mail size={13} style={{ display: "inline", marginRight: 5 }} />Email</label>
              <div style={{ padding: "8px 0", fontSize: 13.5, color: "#94a3b8" }}>
                {user?.email ?? "—"} <span style={{ fontSize: 11 }}>(contact admin to change)</span>
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label"><Phone size={13} style={{ display: "inline", marginRight: 5 }} />Phone</label>
              {editing
                ? <input className="form-input" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+1 (305) 555-0000" />
                : <div style={{ padding: "8px 0", fontSize: 13.5, color: "#1e293b" }}>{user?.phone ?? "—"}</div>
              }
            </div>
            <div className="form-group">
              <label className="form-label"><Briefcase size={13} style={{ display: "inline", marginRight: 5 }} />Job Title</label>
              {editing
                ? <input className="form-input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Sales Representative" />
                : <div style={{ padding: "8px 0", fontSize: 13.5, color: "#1e293b" }}>{user?.title ?? "—"}</div>
              }
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label"><Calendar size={13} style={{ display: "inline", marginRight: 5 }} />Date Joined</label>
              <div style={{ padding: "8px 0", fontSize: 13.5, color: "#1e293b" }}>{formatDate(user?.registered_at ?? user?.created_at)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* At-a-glance stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon blue"><FileCheck size={20} color="#2563eb" /></div>
          <div><div className="stat-label">Docs Signed</div><div className="stat-value">{signedCount}/{totalDocs}</div></div>
        </div>
        <div className="stat-card">
          <div className="stat-icon purple"><BookOpen size={20} color="#7c3aed" /></div>
          <div><div className="stat-label">Modules Done</div><div className="stat-value">{TRAINING_MODULES.filter(m => m.status === "completed").length}/{TRAINING_MODULES.length}</div></div>
        </div>
        <div className="stat-card">
          <div className="stat-icon green"><DollarSign size={20} color="#16a34a" /></div>
          <div><div className="stat-label">YTD Revenue</div><div className="stat-value">{formatCurrency(MOCK_GOALS.ytd_revenue, { compact: true })}</div></div>
        </div>
        <div className="stat-card">
          <div className="stat-icon orange"><TrendingUp size={20} color="#ea580c" /></div>
          <div><div className="stat-label">Loans Closed YTD</div><div className="stat-value">{MOCK_GOALS.ytd_loans}</div></div>
        </div>
      </div>

      {/* Onboarding warning */}
      {!allDocsSigned(user?.onboarding) && (
        <div className="card" style={{ borderColor: "#fde68a", background: "#fffbeb" }}>
          <div className="card-header" style={{ borderColor: "#fde68a" }}>
            <span className="card-title" style={{ color: "#92400e" }}>
              <AlertCircle size={15} style={{ display: "inline", marginRight: 6, color: "#f59e0b" }} />
              Action Required — Complete Your Onboarding
            </span>
          </div>
          <div className="card-body">
            <p style={{ fontSize: 13, color: "#92400e", marginBottom: 14 }}>
              You have unsigned documents. Some features are locked until onboarding is complete.
            </p>
            <div style={{ background: "#e2e8f0", borderRadius: 6, height: 6, marginBottom: 12, overflow: "hidden" }}>
              <div style={{ width: `${onboardingPct}%`, height: "100%", background: "#f59e0b", borderRadius: 6, transition: "width 0.4s" }} />
            </div>
            <p style={{ fontSize: 12, color: "#92400e", marginBottom: 14 }}>{signedCount} of {totalDocs} documents signed</p>
            <button className="btn btn-primary" onClick={() => window.location.href = "/onboarding"}>
              Continue Onboarding <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tab: Documents ───────────────────────────────────────────────────────────

function DocumentsTab({ user, onDocSigned }) {
  const [signed, setSigned] = useState(
    Object.fromEntries(ONBOARDING_DOCS.map(d => [d.docKey, user?.onboarding?.[d.onboardingKey] ?? false]))
  );

  function handleSigned(docKey) {
    setSigned(s => ({ ...s, [docKey]: true }));
    onDocSigned?.(docKey);
  }

  const signedCount = Object.values(signed).filter(Boolean).length;
  const total       = ONBOARDING_DOCS.length;
  const allSigned   = signedCount === total;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {SPIN_STYLE}

      <div className="card">
        <div className="card-header">
          <span className="card-title">Onboarding Documents</span>
          {allSigned
            ? <span className="badge badge-green"><Check size={12} /> All signed</span>
            : <span className="badge badge-proposal">{signedCount}/{total} signed</span>
          }
        </div>
        <div className="card-body">
          {!allSigned && (
            <>
              <p style={{ fontSize: 13, color: "#64748b", marginBottom: 8 }}>
                All {total} documents are required before your account is fully activated.
                Click <strong>Sign Now</strong> on any pending document — the signing window opens right here.
              </p>
              <div style={{ background: "#e2e8f0", borderRadius: 6, height: 6, marginBottom: 20, overflow: "hidden" }}>
                <div style={{ width: `${Math.round((signedCount / total) * 100)}%`, height: "100%", background: "#2563eb", borderRadius: 6, transition: "width 0.4s" }} />
              </div>
            </>
          )}

          {allSigned && (
            <div className="agent-banner" style={{ marginBottom: 20 }}>
              <CheckCircle size={18} color="#16a34a" style={{ flexShrink: 0 }} />
              <div className="agent-banner-text">
                <strong>All documents signed</strong>
                <span>Your account is fully activated. Training and goal-setting are now unlocked.</span>
              </div>
            </div>
          )}

          {ONBOARDING_DOCS.map(({ docKey, docLabel, templateId }) => (
            <SignNowStep
              key={docKey}
              docKey={docKey}
              docLabel={docLabel}
              templateId={templateId}
              signerEmail={user?.email ?? ""}
              signerName={user?.full_name ?? ""}
              onSigned={handleSigned}
              signed={signed[docKey]}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Tab: Training ────────────────────────────────────────────────────────────

function TrainingTab({ locked }) {
  const [activeModule, setActiveModule] = useState(null);
  const completedCount = TRAINING_MODULES.filter(m => m.status === "completed").length;
  const progressPct    = Math.round((completedCount / TRAINING_MODULES.length) * 100);

  if (locked) {
    return <LockedPlaceholder title="Training Locked" desc="Complete your onboarding documents to unlock training materials and your roadmap." />;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      {/* Progress bar */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">Training Progress</span>
          <span className="badge badge-blue">{completedCount}/{TRAINING_MODULES.length} complete</span>
        </div>
        <div className="card-body">
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 6 }}>
            <div style={{ flex: 1, background: "#e2e8f0", borderRadius: 6, height: 8, overflow: "hidden" }}>
              <div style={{ width: `${progressPct}%`, height: "100%", background: "#2563eb", borderRadius: 6, transition: "width 0.5s" }} />
            </div>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#2563eb", flexShrink: 0 }}>{progressPct}%</span>
          </div>
          <p style={{ fontSize: 12, color: "#94a3b8" }}>
            {TRAINING_MODULES.filter(m => m.status === "locked").length} modules remaining
          </p>
        </div>
      </div>

      {/* Module list */}
      <div className="card">
        <div className="card-header"><span className="card-title">Training Modules</span></div>
        <div className="card-body" style={{ paddingTop: 8 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {TRAINING_MODULES.map(mod => {
              const isOpen       = activeModule === mod.id;
              const isLocked     = mod.status === "locked";
              const isCompleted  = mod.status === "completed";
              const isInProgress = mod.status === "in_progress";

              return (
                <div key={mod.id}
                  className="pipeline-card"
                  style={{
                    borderLeft: `3px solid ${isLocked ? "#e2e8f0" : mod.color}`,
                    opacity: isLocked ? 0.65 : 1,
                    cursor: isLocked ? "default" : "pointer",
                    background: isOpen ? "#f8fafc" : "#fff",
                  }}
                  onClick={() => !isLocked && setActiveModule(isOpen ? null : mod.id)}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
                      background: isCompleted ? "#f0fdf4" : isInProgress ? "#eff6ff" : "#f1f5f9",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      {isCompleted  && <CheckCircle size={16} color="#16a34a" />}
                      {isInProgress && <Clock size={16} color="#2563eb" />}
                      {isLocked     && <Lock size={14} color="#94a3b8" />}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span className="pipeline-card-name">{mod.title}</span>
                        {mod.required && <span className="badge badge-lost" style={{ fontSize: 10 }}>Required</span>}
                      </div>
                      <div className="pipeline-card-sub">{mod.subtitle}</div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                      <span style={{ fontSize: 11, color: "#94a3b8" }}>{mod.duration}</span>
                      {isCompleted  && <span className="badge badge-green"   style={{ fontSize: 10 }}>Done</span>}
                      {isInProgress && <span className="badge badge-blue"    style={{ fontSize: 10 }}>In Progress</span>}
                      {isLocked     && <span className="badge badge-outline" style={{ fontSize: 10 }}>Locked</span>}
                    </div>
                  </div>

                  {isOpen && (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #f1f5f9" }}>
                      <p style={{ fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.04em" }}>Lessons</p>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {mod.lessons.map((lesson, li) => (
                          <div key={li} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 10px", background: "#f8fafc", borderRadius: 6 }}>
                            {isCompleted
                              ? <Check size={13} color="#16a34a" />
                              : <div style={{ width: 13, height: 13, borderRadius: "50%", border: "1.5px solid #cbd5e1" }} />
                            }
                            <span style={{ fontSize: 13, color: "#475569" }}>{lesson}</span>
                          </div>
                        ))}
                      </div>
                      {!isCompleted && (
                        <button className="btn btn-primary btn-sm" style={{ marginTop: 12 }}>
                          {isInProgress ? "Continue Module" : "Start Module"} <ChevronRight size={13} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Roadmap */}
      <div className="card">
        <div className="card-header">
          <span className="card-title"><Map size={15} style={{ display: "inline", marginRight: 6 }} />Ramp Roadmap</span>
        </div>
        <div className="card-body">
          <p style={{ fontSize: 13, color: "#64748b", marginBottom: 20 }}>Your 90-day path from onboarding to fully ramped.</p>
          <div style={{ position: "relative", paddingLeft: 28 }}>
            <div style={{ position: "absolute", left: 10, top: 6, bottom: 6, width: 2, background: "#e2e8f0" }} />
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              {ROADMAP_MILESTONES.map((m, i) => (
                <div key={i} style={{ position: "relative", display: "flex", gap: 14 }}>
                  <div style={{
                    position: "absolute", left: -28, top: 2,
                    width: 20, height: 20, borderRadius: "50%",
                    background: m.done ? "#2563eb" : "#fff",
                    border: `2px solid ${m.done ? "#2563eb" : "#cbd5e1"}`,
                    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1,
                  }}>
                    {m.done && <Check size={11} color="#fff" />}
                  </div>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: m.done ? "#2563eb" : "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>{m.week}</span>
                      <span style={{ fontSize: 13.5, fontWeight: 600, color: m.done ? "#0f172a" : "#475569" }}>{m.label}</span>
                      {m.done && <span className="badge badge-green" style={{ fontSize: 10 }}>Complete</span>}
                    </div>
                    <p style={{ fontSize: 13, color: "#64748b", margin: 0 }}>{m.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}

// ─── Tab: Goals ───────────────────────────────────────────────────────────────

function GoalsTab({ locked }) {
  const g = MOCK_GOALS;

  const [goals, setGoals] = useState({
    monthly_calls:   g.monthly_calls,
    monthly_loans:   g.monthly_loans,
    monthly_revenue: g.monthly_revenue,
  });
  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState({ ...goals });
  const [saved,   setSaved]   = useState(false);

  if (locked) {
    return <LockedPlaceholder title="Goals Locked" desc="Complete your onboarding to set and track your performance goals." />;
  }

  const metrics = [
    { label: "Discovery Calls",   icon: <Phone    size={18} color="#2563eb" />, cls: "blue",   current: g.current_month_calls,   target: goals.monthly_calls,   key: "monthly_calls",   format: v => `${v}` },
    { label: "Loans Closed",      icon: <FileCheck size={18} color="#7c3aed" />, cls: "purple", current: g.current_month_loans,   target: goals.monthly_loans,   key: "monthly_loans",   format: v => `${v}` },
    { label: "Revenue Generated", icon: <DollarSign size={18} color="#16a34a" />, cls: "green", current: g.current_month_revenue, target: goals.monthly_revenue, key: "monthly_revenue", format: v => formatCurrency(v, { compact: true }) },
  ];

  function handleSave() {
    setGoals({ ...draft });
    setEditing(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      {/* Monthly targets */}
      <div className="card">
        <div className="card-header">
          <span className="card-title"><Target size={15} style={{ display: "inline", marginRight: 6 }} />Monthly Targets</span>
          {!editing
            ? <button className="btn btn-secondary btn-sm" onClick={() => { setDraft({ ...goals }); setEditing(true); }}><Edit2 size={13} /> Edit Goals</button>
            : <div style={{ display: "flex", gap: 8 }}>
                <button className="btn btn-primary btn-sm" onClick={handleSave}><Save size={13} /> Save</button>
                <button className="btn btn-secondary btn-sm" onClick={() => setEditing(false)}><X size={13} /> Cancel</button>
              </div>
          }
        </div>
        <div className="card-body">
          {saved && <p className="settings-success" style={{ marginBottom: 12 }}><Check size={14} />Goals updated</p>}
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {metrics.map(m => {
              const p        = formatPct(m.current, m.target);
              const barColor = p >= 100 ? "#16a34a" : p >= 60 ? "#2563eb" : p >= 30 ? "#f59e0b" : "#ef4444";
              return (
                <div key={m.key}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div className={`stat-icon ${m.cls}`} style={{ width: 34, height: 34, borderRadius: 8 }}>{m.icon}</div>
                      <div>
                        <div style={{ fontSize: 13.5, fontWeight: 600, color: "#0f172a" }}>{m.label}</div>
                        <div style={{ fontSize: 12, color: "#64748b" }}>
                          {m.format(m.current)} of{" "}
                          {editing
                            ? <input type="number" value={draft[m.key]}
                                onChange={e => setDraft(d => ({ ...d, [m.key]: Number(e.target.value) }))}
                                style={{ width: 72, padding: "1px 6px", border: "1px solid #2563eb", borderRadius: 4, fontSize: 12, color: "#0f172a" }} />
                            : m.format(m.target)
                          }{" "}target
                        </div>
                      </div>
                    </div>
                    <span style={{ fontSize: 16, fontWeight: 700, color: p >= 100 ? "#16a34a" : "#0f172a" }}>{p}%</span>
                  </div>
                  <div style={{ background: "#f1f5f9", borderRadius: 6, height: 8, overflow: "hidden" }}>
                    <div style={{ width: `${p}%`, height: "100%", background: barColor, borderRadius: 6, transition: "width 0.4s" }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* YTD summary */}
      <div className="card">
        <div className="card-header">
          <span className="card-title"><BarChart2 size={15} style={{ display: "inline", marginRight: 6 }} />Year-to-Date Summary</span>
        </div>
        <div className="card-body">
          <div className="stats-grid">
            <div className="stat-card"><div className="stat-icon blue"><Phone size={20} color="#2563eb" /></div><div><div className="stat-label">Total Calls</div><div className="stat-value">{g.ytd_calls}</div></div></div>
            <div className="stat-card"><div className="stat-icon purple"><FileCheck size={20} color="#7c3aed" /></div><div><div className="stat-label">Loans Closed</div><div className="stat-value">{g.ytd_loans}</div></div></div>
            <div className="stat-card"><div className="stat-icon green"><DollarSign size={20} color="#16a34a" /></div><div><div className="stat-label">Revenue</div><div className="stat-value">{formatCurrency(g.ytd_revenue, { compact: true })}</div></div></div>
            <div className="stat-card"><div className="stat-icon orange"><TrendingUp size={20} color="#ea580c" /></div><div><div className="stat-label">Conversion Rate</div><div className="stat-value">{g.ytd_loans > 0 ? Math.round((g.ytd_loans / g.ytd_calls) * 100) : 0}%</div></div></div>
          </div>
        </div>
      </div>

      {/* Achievements */}
      <div className="card">
        <div className="card-header">
          <span className="card-title"><Award size={15} style={{ display: "inline", marginRight: 6 }} />Achievements</span>
        </div>
        <div className="card-body">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 12 }}>
            {[
              { label: "First Close",       earned: true,  icon: "🏆", desc: "Closed your first deal" },
              { label: "Call Machine",      earned: true,  icon: "📞", desc: "100+ calls in a month" },
              { label: "Document Hero",     earned: false, icon: "📄", desc: "All docs signed on day 1" },
              { label: "Double Dipper",     earned: false, icon: "💰", desc: "2 closes in one month" },
              { label: "Training Complete", earned: false, icon: "🎓", desc: "Finished all modules" },
              { label: "90-Day Ramped",     earned: false, icon: "🚀", desc: "Hit target in first 90 days" },
            ].map(a => (
              <div key={a.label} style={{
                border: "1px solid", borderColor: a.earned ? "#bfdbfe" : "#e2e8f0",
                borderRadius: 10, padding: "14px 12px", textAlign: "center",
                background: a.earned ? "#eff6ff" : "#f8fafc", opacity: a.earned ? 1 : 0.55,
              }}>
                <div style={{ fontSize: 24, marginBottom: 6 }}>{a.icon}</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: a.earned ? "#1e40af" : "#64748b", marginBottom: 4 }}>{a.label}</div>
                <div style={{ fontSize: 11, color: "#94a3b8" }}>{a.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const TABS = [
  { key: "overview",  label: "Overview",  icon: User     },
  { key: "documents", label: "Documents", icon: FileText  },
  { key: "training",  label: "Training",  icon: BookOpen  },
  { key: "goals",     label: "Goals",     icon: Target    },
];

export default function Profile() {
  // TODO: swap MOCK_USER for useAuth() once role gating is ready
  const [user, setUser]           = useState(MOCK_USER);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    fetch("/api/me", { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { if (!d.error) setUser(d); })
      .catch(() => {});
  }, []);

  // Bubble a signed doc up so tab locks update immediately without a page reload
  function handleDocSigned(docKey) {
    const onboardingKey = docKeyToOnboardingKey(docKey);
    if (!onboardingKey) return;
    setUser(u => ({ ...u, onboarding: { ...u.onboarding, [onboardingKey]: true } }));
  }

  const docsLocked = !allDocsSigned(user?.onboarding);

  return (
    <div className="page">
      <div className="page-header">
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <Avatar name={user?.full_name} email={user?.email} size={48} />
          <div>
            <div className="page-title">{user?.full_name ?? "My Profile"}</div>
            <div className="page-subtitle">{user?.title ?? "Sales Representative"} · {user?.email}</div>
          </div>
        </div>
      </div>

      <div className="settings-tabs-bar">
        {TABS.map(({ key, label, icon: Icon }) => {
          const isLocked = (key === "training" || key === "goals") && docsLocked;
          return (
            <button key={key}
              className={`settings-top-tab ${activeTab === key ? "active" : ""}`}
              onClick={() => setActiveTab(key)}
              title={isLocked ? "Complete onboarding to unlock" : undefined}
            >
              <Icon size={14} />{label}
              {isLocked && <Lock size={11} style={{ marginLeft: 2, color: "#94a3b8" }} />}
            </button>
          );
        })}
      </div>

      <div className="settings-tab-content" style={{ maxWidth: 780 }}>
        {activeTab === "overview"  && <OverviewTab  user={user} onEditSave={updates => setUser(u => ({ ...u, ...updates }))} />}
        {activeTab === "documents" && <DocumentsTab user={user} onDocSigned={handleDocSigned} />}
        {activeTab === "training"  && <TrainingTab  locked={docsLocked} />}
        {activeTab === "goals"     && <GoalsTab     locked={docsLocked} />}
      </div>
    </div>
  );
}